<script setup lang="ts">
import { computed } from 'vue';

/**
 * Self-hosted Material Symbols (Outlined) icon, matching the Stitch design source
 * (scratchpad/stitch/tokens.md: "Material Symbols (outlined) icon set throughout").
 * ADR-054 follow-up: the app has been swept to use this everywhere instead of PrimeIcons
 * (`pi pi-*` classes) — a Vitest guard (`apps/web/src/no-primeicons.spec.ts`) asserts no `pi pi-`
 * class usage remains in `apps/web/src`. `packages/ui`'s own `Button` wrapper (`Button.vue`) and
 * `Menu`'s `#itemicon` slot render this too, so PrimeVue components that take an `icon` prop can
 * render a Material Symbol without a repo-wide component swap.
 *
 * `name` is a Material Symbols ligature name, e.g. "dashboard", "notifications",
 * "language", "settings" — see https://fonts.google.com/icons for the full catalog.
 */
const props = withDefaults(
  defineProps<{
    name: string;
    /** Pixel size for both font-size and the bounding box. Default 24 (the font's own default). */
    size?: number;
    /** Use the filled (solid) variant instead of the default outline. */
    filled?: boolean;
  }>(),
  { size: 24, filled: false },
);

const style = computed(() => ({
  fontSize: `${props.size}px`,
  fontVariationSettings: props.filled ? "'FILL' 1" : "'FILL' 0",
}));
</script>

<template>
  <span aria-hidden="true" class="material-symbols-outlined jv-icon" :style="style">{{ name }}</span>
</template>

<style scoped>
.jv-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}
</style>
